import { Checkbox, HStack, LightMode, Text } from "@chakra-ui/react"
import UserAvatar from "@pathwright/ux/src/components/avatar/UserAvatar"
import {
  FormatOptionLabelMeta,
  GroupBase,
  MultiValue,
  OptionBase,
  OptionProps,
  Props,
  Select,
  SingleValue,
  useChakraSelectProps
} from "chakra-react-select"
import { useEffect, useMemo, useRef, useState } from "react"
import { usePreviousDistinct } from "react-use"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import RoleBadge, {
  RoleOption,
  getRoleOptions,
  registrationRoleEnumMap,
  serializeRoleBadgesToRegistrationRoles,
  sortRoleOptionsByRole
} from "./RoleBadge"
import AtmosphericBackground from "./components/AtmosphericBackground"
import {
  CustomSelectProps,
  getSelectCustomComponents
} from "./components/select"
import { CohortRole, ResourceRole } from "./state"

interface SelectRoleOption extends RoleOption, OptionBase {}

const selectComponents = getSelectCustomComponents<
  SelectRoleOption,
  boolean,
  SelectCustomProps
>()

const MultiValueRemove = (
  props: Parameters<typeof selectComponents.MultiValueRemove>[0]
) =>
  // Only allowing option to be cleared if not found in initially selected options.
  isOptionClearable({
    option: props.data,
    initiallySelectedOptions: props.selectProps._initiallySelectedOptions
  }) ? (
    <selectComponents.MultiValueRemove {...props} />
  ) : null

// Add learn more link to menu.
const Menu = (props: Parameters<typeof selectComponents.Menu>[0]) => {
  return (
    <LightMode>
      <selectComponents.Menu {...props}>
        <AtmosphericBackground image={props.selectProps._atmosphericImage}>
          {props.children}
        </AtmosphericBackground>
      </selectComponents.Menu>
    </LightMode>
  )
}

// Ensure we render the menu in light mode.
const Option = (
  props: OptionProps<SelectRoleOption, boolean, GroupBase<SelectRoleOption>> & {
    selectProps: SelectCustomProps
  }
) => {
  const { data, innerRef, innerProps, selectOption } = props

  function getIsOptionClearable(option: SelectRoleOption) {
    return isOptionClearable({
      option,
      initiallySelectedOptions: props.selectProps._initiallySelectedOptions
    })
  }

  // Using custom option component to avoid issue with react-select when using
  // combination of isMulti, !hideSelectedOptions, and !closeMenuOnSelect. This
  // config actually ends up closing the menu when clicking on an option,
  // which is not the desired behavior.
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={(e) => {
        e.preventDefault()
        if (getIsOptionClearable(props.data)) {
          selectOption(data)
        }
      }}
    >
      {props.children}
    </div>
  )
}

// Pass any custom props to components
type SelectCustomProps = {
  _initiallySelectedOptions?: SelectRoleOption[]
  _atmosphericImage?: string
} & CustomSelectProps

// Note: Typescript is not able to infer types for chakra-react-select some reason.
// Have to explicitly type the generics: https://react-select.com/typescript
type SelectProps = Props<
  SelectRoleOption,
  boolean,
  GroupBase<SelectRoleOption>
> &
  SelectCustomProps

function useCustomChakraSelectProps(): SelectProps {
  return useChakraSelectProps({
    chakraStyles: {
      container: (provided, state) => ({
        ...provided,
        w: "100%"
      }),
      option: (provided, state) => ({
        ...provided,
        color: "gray.900",
        w: "initial",
        paddingInline: ".4rem",
        bg: "transparent",
        _hover: {
          bg: "transparent"
        },
        opacity: state.isFocused ? 0.8 : 1,
        _disabled: {
          cursor: "not-allowed",
          opacity: state.isSelected ? 1 : 0.4
        }
      }),
      menu: (provided, state) => ({
        ...provided,
        bg: "transparent",
        borderRadius: "xl",
        // Hide overflowing bg of focused/selected items at start/end of menu.
        overflow: "hidden"
      }),
      menuList: (provided, state) => ({
        ...provided,
        padding: 2,
        border: "none",
        bg: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start"
      }),
      placeholder: (provided) => ({
        ...provided,
        paddingLeft: "2px",
        fontSize: { base: "sm", md: undefined },
        noOfLines: 1
      }),
      control: (provided, state) => ({
        ...provided,
        padding: "0 .4em",
        border: "1px solid",
        borderColor: "gray.100",
        color: "gray.900",
        borderRadius: "20px"
      }),
      clearIndicator: (provided) => ({
        ...provided,
        m: 0,
        mr: 1
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        // Make room for the tooltip icon to the right of the dropdown indicator.
        mr: `calc(var(--input-height) - var(--input-padding))`
      }),
      valueContainer: (provided) => ({
        ...provided,
        pl: 0
      }),
      multiValueRemove: (provided) => ({
        ...provided,
        ml: 0
      })
    }
  })
}

function isOptionClearable({
  option,
  initiallySelectedOptions
}: {
  option: SelectRoleOption
  initiallySelectedOptions?: SelectRoleOption[]
}) {
  // User can clear option as long as it isn't found in the initial values.
  const isClearable = initiallySelectedOptions?.every(
    (selectedOption) => option.value !== selectedOption.value
  )

  return Boolean(isClearable)
}

type Roles = {
  resourceRole: ResourceRole
  cohortRole: CohortRole
}

export type RoleSelectorOnChange = (value: Roles) => void

type RoleSelectorProps = {
  value: Roles
  onChange: RoleSelectorOnChange
  atmosphericImage?: string
} & Omit<SelectProps, "onChange" | "value">

function RoleSelector({
  value,
  onChange,
  atmosphericImage,
  ...selectProps
}: RoleSelectorProps) {
  const [selectedOptions, setSelectedOptions] = useState<SelectRoleOption[]>([])
  const chakraSelectProps = useCustomChakraSelectProps()
  const options = getRoleOptions()
  const { me } = usePathwrightContext()

  function formatOptionLabel(
    option: SelectRoleOption,
    meta: FormatOptionLabelMeta<SelectRoleOption>
  ) {
    // // Contextualize the option label, more verbose version used in menu.
    // return meta.context === "menu" ? null : (
    //   <RoleBadge badge={option.badge} />
    // )

    const isSelected = meta.selectValue.some(
      (selectedOption) => selectedOption.value === option.value
    )

    if (meta.context === "menu") {
      return (
        <Checkbox
          isChecked={isSelected}
          // NOTE: this is not working due to the initiallySelectedOptionsRef always being empty.
          // Likely due to recent refactoring to the PathCreatorForm.
          isDisabled={
            !isOptionClearable({
              option,
              initiallySelectedOptions: initiallySelectedOptionsRef.current
            })
          }
          px={2}
        >
          <HStack>
            <RoleBadge badge={option.badge} highlight={isSelected} />
            <Text as="span">{option.description}</Text>
          </HStack>
        </Checkbox>
      )
    }

    return <RoleBadge badge={option.badge} highlight={isSelected} />
  }

  // Ensure the selected options contain the non-clearable options.
  function handleChange(
    selected: SingleValue<SelectRoleOption> | MultiValue<SelectRoleOption>
  ) {
    // Type as selected options, forcing array.
    let nextSelectedOptions = selected as SelectRoleOption[]

    // Add back any previously selected option that is not clearable,
    // preventing multi-remove from removing non-clearable options.
    const nonClearableOptions = selectedOptions
      // Keep options that aren't clearable.
      .filter(
        (option) =>
          !isOptionClearable({
            option,
            initiallySelectedOptions: initiallySelectedOptionsRef.current
          })
      )
      // Keep options that aren't already selected.
      .filter(
        (option) =>
          !nextSelectedOptions.some(
            (selectedOption) => selectedOption.value === option.value
          )
      )

    // Merge the two lists.
    const mergedSelectedOptions = [
      ...nextSelectedOptions,
      ...nonClearableOptions
    ]

    // Finally, set the selected options.
    setSelectedOptions(sortRoleOptionsByRole(mergedSelectedOptions))
  }

  const preivousSelected = usePreviousDistinct(selectedOptions)

  // Whenever the selected options change, fire an onChange callback with the roles.
  // We must serialize the selected options back into registration roles.
  useEffect(() => {
    // Only call onChange when local state has changed from initial.
    if (preivousSelected) {
      onChange(serializeRoleBadgesToRegistrationRoles(selectedOptions))
    }
  }, [selectedOptions])

  // Get the selected options
  function getSelectedOptions(roles: Roles) {
    const { resourceRole, cohortRole } = roles

    const selectedOptions = new Set<RoleOption>([])

    if (resourceRole) {
      for (const roleOption of registrationRoleEnumMap.get(resourceRole)!
        .options) {
        selectedOptions.add(roleOption)
      }
    }
    if (cohortRole) {
      for (const roleOption of registrationRoleEnumMap.get(cohortRole)!
        .options) {
        selectedOptions.add(roleOption)
      }
    }

    return sortRoleOptionsByRole([...selectedOptions])
  }

  // Sync local state with parent.
  useEffect(() => {
    setSelectedOptions(getSelectedOptions(value))
  }, [value.resourceRole, value.cohortRole])

  // Store the initially selected options so that we can ensure
  // they are not removable. This means that a user can not unselect
  // a role they already have in a path/cohort.
  const initiallySelectedOptionsRef = useRef(
    useMemo(() => getSelectedOptions(value), [])
  )

  // Determine if user can clear the select input based on if there are any
  // currently selected options that are clearable.
  const isClearable = useMemo(() => {
    return selectedOptions.some((option) =>
      isOptionClearable({
        option,
        initiallySelectedOptions: initiallySelectedOptionsRef.current
      })
    )
  }, [value])

  return (
    <Select
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      formatOptionLabel={formatOptionLabel}
      aria-label="Select roles"
      placeholder="Select your roles"
      variant="unstyled"
      isClearable={false}
      escapeClearsValue
      hideSelectedOptions={false}
      isMulti={true}
      isSearchable={false}
      autoFocus={!selectProps.isDisabled}
      openMenuOnFocus
      closeMenuOnSelect={false}
      // custom select props
      _initiallySelectedOptions={initiallySelectedOptionsRef.current}
      _atmosphericImage={atmosphericImage}
      renderControlLeftElement={() => <UserAvatar user={me} size="xs" />}
      {...{
        ...chakraSelectProps,
        components: {
          ...chakraSelectProps,
          ...selectComponents,
          // Locally custom components.
          // MultiValueRemove,
          Menu,
          Option,
          MultiValueRemove: () => null
        }
      }}
      {...selectProps}
    />
  )
}

export default RoleSelector
